import {Fields, Submenu} from './_models'

type FormEntry = {
  label: string
  value: any
}

export type ReportFormData = {
  [tabId: number]: {
    [fieldId: string]: FormEntry
  }
}

type VoyageMetrics = {
  seaDaysByQuarter: number[]
  portDaysByQuarter: number[]
  annualSeaDays: number
  annualPortDays: number
}

type QuarterSegment = {
  quarterIndex: number
  ratio: number
}

const normalize = (value: string | undefined | null) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()

const roundTo2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100

const getStoredValue = (formData: ReportFormData, tabId: number, fieldId: number) =>
  formData?.[tabId]?.[String(fieldId)]?.value

const sortedFields = (fields: Fields[] = []) =>
  [...fields].sort((a, b) => {
    const orderA = a.order ?? Number.MAX_SAFE_INTEGER
    const orderB = b.order ?? Number.MAX_SAFE_INTEGER
    if (orderA !== orderB) return orderA - orderB
    return a.id - b.id
  })

const isVoyageTab = (tab: Submenu) => normalize(tab.name).includes('voyage')

const isQuarterlySummaryTab = (tab: Submenu) => {
  const name = normalize(tab.name)
  return name.includes('quarterly') || (name.includes('cii') && name.includes('summary'))
}

const isAnnualSummaryTab = (tab: Submenu) => normalize(tab.name).includes('annual')

const parseNumber = (value: any): number | null => {
  if (value === undefined || value === null || value === '') return null
  const parsed = Number(String(value).replace(/,/g, '').trim())
  return Number.isFinite(parsed) ? parsed : null
}

const parseDate = (value: any): Date | null => {
  if (!value) return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value

  const text = String(value).trim()
  if (!text) return null

  const nativeParsed = new Date(text)
  if (!Number.isNaN(nativeParsed.getTime())) return nativeParsed

  const match = text.match(
    /^(\d{1,2})[\/-](\d{1,2}|[A-Za-z]{3})[\/-](\d{2,4})(?:\s+(\d{1,2}):(\d{2}))?$/
  )
  if (!match) return null

  const [, dayText, monthText, yearText, hourText, minuteText] = match
  const day = Number(dayText)
  const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
  const month = /^\d+$/.test(monthText)
    ? Number(monthText) - 1
    : monthNames.indexOf(monthText.toLowerCase())
  if (month < 0 || month > 11) return null

  let year = Number(yearText)
  if (yearText.length === 2) year += year >= 70 ? 1900 : 2000

  const hours = hourText ? Number(hourText) : 0
  const minutes = minuteText ? Number(minuteText) : 0
  const parsed = new Date(year, month, day, hours, minutes)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const getQuarterIndex = (date: Date | null) => {
  if (!date) return null
  return Math.floor(date.getMonth() / 3)
}

const getQuarterBoundaryMidnightMs = (year: number) => [
  new Date(year, 3, 1, 0, 0, 0, 0).getTime(),
  new Date(year, 6, 1, 0, 0, 0, 0).getTime(),
  new Date(year, 9, 1, 0, 0, 0, 0).getTime(),
  new Date(year + 1, 0, 1, 0, 0, 0, 0).getTime(),
].filter((time) => !Number.isNaN(time))

const getQuarterIndexForRange = (fromDate: Date | null, toDate: Date | null) => {
  if (fromDate && toDate && toDate.getTime() > fromDate.getTime()) {
    return getQuarterIndex(new Date(toDate.getTime() - 1))
  }
  return getQuarterIndex(toDate || fromDate)
}

const getQuarterSegments = (fromDate: Date | null, toDate: Date | null): QuarterSegment[] => {
  if (!fromDate || !toDate) return []

  const startMs = fromDate.getTime()
  const endMs = toDate.getTime()
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return []

  const boundaries: number[] = []
  for (let year = fromDate.getFullYear() - 1; year <= toDate.getFullYear() + 1; year += 1) {
    boundaries.push(...getQuarterBoundaryMidnightMs(year))
  }

  const cuts = boundaries
    .filter((time) => time > startMs && time < endMs)
    .sort((a, b) => a - b)

  const totalDurationMs = endMs - startMs
  const segments: QuarterSegment[] = []
  let segmentStart = startMs

  for (const cut of [...cuts, endMs]) {
    const segmentDurationMs = cut - segmentStart
    if (segmentDurationMs <= 0) {
      segmentStart = cut
      continue
    }

    const quarterIndex = getQuarterIndex(new Date(cut - 1))
    if (quarterIndex !== null) {
      segments.push({
        quarterIndex,
        ratio: segmentDurationMs / totalDurationMs,
      })
    }
    segmentStart = cut
  }

  return segments
}

const getEventDayBucket = (eventType: string): 'sea' | 'port' | null => {
  if (eventType.includes('sailing') || eventType.includes('drifting') || eventType.includes('sea')) return 'sea'
  if (eventType.includes('anchor') || eventType.includes('berth') || eventType.includes('transfer')) {
    return 'port'
  }
  return null
}

const fieldValuesByMatcher = (
  tab: Submenu,
  formData: ReportFormData,
  matcher: (label: string) => boolean
) =>
  sortedFields(tab.fields || [])
    .filter((field) => field.isActive && matcher(normalize(field.label)))
    .map((field) => ({
      field,
      value: getStoredValue(formData, tab.id, field.id),
    }))

const buildVoyageMetrics = (tabs: Submenu[], formData: ReportFormData): VoyageMetrics => {
  const seaDaysByQuarter = [0, 0, 0, 0]
  const portDaysByQuarter = [0, 0, 0, 0]

  const voyageTabs = tabs.filter(isVoyageTab)
  voyageTabs.forEach((tab) => {
    const fromDates = fieldValuesByMatcher(
      tab,
      formData,
      (label) => label.includes('from') && label.includes('utc') && label.includes('date')
    )
    const toDates = fieldValuesByMatcher(
      tab,
      formData,
      (label) => label.includes('to') && label.includes('utc') && label.includes('date')
    )
    const eventTypes = fieldValuesByMatcher(tab, formData, (label) => label.includes('event type'))
    const hoursAtSea = fieldValuesByMatcher(tab, formData, (label) => label.includes('hours at sea'))
    const hoursAtPort = fieldValuesByMatcher(
      tab,
      formData,
      (label) => label.includes('hours at anchor') || label.includes('hours at berth')
    )

    const rowCount = Math.max(
      fromDates.length,
      toDates.length,
      eventTypes.length,
      hoursAtSea.length,
      hoursAtPort.length
    )

    for (let index = 0; index < rowCount; index += 1) {
      const fromDate = parseDate(fromDates[index]?.value)
      const toDate = parseDate(toDates[index]?.value)
      const eventType = normalize(eventTypes[index]?.value)
      const quarterIndex = getQuarterIndexForRange(fromDate, toDate)

      if (quarterIndex === null) continue

      const explicitSeaHours = parseNumber(hoursAtSea[index]?.value)
      const explicitPortHours = parseNumber(hoursAtPort[index]?.value)
      const durationHours =
        fromDate && toDate ? Math.max(0, (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60)) : null
      const eventDayBucket = getEventDayBucket(eventType)

      const seaHours =
        explicitSeaHours !== null
          ? explicitSeaHours
          : durationHours !== null && eventDayBucket === 'sea'
            ? durationHours
            : 0
      const portHours =
        explicitPortHours !== null
          ? explicitPortHours
          : durationHours !== null && eventDayBucket === 'port'
            ? durationHours
            : 0

      const quarterSegments = getQuarterSegments(fromDate, toDate)
      if (quarterSegments.length > 0) {
        quarterSegments.forEach((segment) => {
          seaDaysByQuarter[segment.quarterIndex] += (seaHours * segment.ratio) / 24
          portDaysByQuarter[segment.quarterIndex] += (portHours * segment.ratio) / 24
        })
        continue
      }

      seaDaysByQuarter[quarterIndex] += seaHours / 24
      portDaysByQuarter[quarterIndex] += portHours / 24
    }
  })

  return {
    seaDaysByQuarter: seaDaysByQuarter.map(roundTo2),
    portDaysByQuarter: portDaysByQuarter.map(roundTo2),
    annualSeaDays: roundTo2(seaDaysByQuarter.reduce((sum, value) => sum + value, 0)),
    annualPortDays: roundTo2(portDaysByQuarter.reduce((sum, value) => sum + value, 0)),
  }
}

const quarterIndexFromLabel = (label: string): number | null => {
  if (label.includes('1st quarter') || label.includes('first quarter') || label.includes('q1')) return 0
  if (label.includes('2nd quarter') || label.includes('second quarter') || label.includes('q2')) return 1
  if (label.includes('3rd quarter') || label.includes('third quarter') || label.includes('q3')) return 2
  if (label.includes('4th quarter') || label.includes('fourth quarter') || label.includes('q4')) return 3
  return null
}

const isSeaDayField = (label: string) =>
  (label.includes('sailing') || label.includes('drifting') || label.includes('spent at sea')) &&
  !label.includes('fuel')

const isPortDayField = (label: string) =>
  (label.includes('berth') || label.includes('anchor') || label.includes('spent in port')) &&
  !label.includes('fuel')

const asDisplayNumber = (value: number) => roundTo2(value).toFixed(2)

export const applyCiiSummaryCalculations = (
  tabs: Submenu[],
  formData: ReportFormData
): ReportFormData => {
  if (!tabs.length) return formData

  const metrics = buildVoyageMetrics(tabs, formData)
  let hasChanges = false
  const nextFormData: ReportFormData = {...formData}

  const upsertValue = (tabId: number, field: Fields, value: string) => {
    const existingValue = nextFormData?.[tabId]?.[String(field.id)]?.value
    if (String(existingValue ?? '') === value) return

    hasChanges = true
    nextFormData[tabId] = {
      ...(nextFormData[tabId] || {}),
      [String(field.id)]: {
        label: field.label,
        value,
      },
    }
  }

  tabs
    .filter(isQuarterlySummaryTab)
    .forEach((tab) => {
      const orderedFields = sortedFields(tab.fields || []).filter((field) => field.isActive)
      const seaFields = orderedFields.filter((field) => isSeaDayField(normalize(field.label)))
      const portFields = orderedFields.filter((field) => isPortDayField(normalize(field.label)))

      seaFields.forEach((field, index) => {
        const label = normalize(field.label)
        const quarterIndex = quarterIndexFromLabel(label) ?? (seaFields.length >= 4 ? index : null)
        if (quarterIndex === null || quarterIndex > 3) return
        upsertValue(tab.id, field, asDisplayNumber(metrics.seaDaysByQuarter[quarterIndex]))
      })

      portFields.forEach((field, index) => {
        const label = normalize(field.label)
        const quarterIndex = quarterIndexFromLabel(label) ?? (portFields.length >= 4 ? index : null)
        if (quarterIndex === null || quarterIndex > 3) return
        upsertValue(tab.id, field, asDisplayNumber(metrics.portDaysByQuarter[quarterIndex]))
      })
    })

  tabs
    .filter(isAnnualSummaryTab)
    .forEach((tab) => {
      sortedFields(tab.fields || [])
        .filter((field) => field.isActive)
        .forEach((field) => {
          const label = normalize(field.label)
          if (label.includes('spent at sea')) {
            upsertValue(tab.id, field, asDisplayNumber(metrics.annualSeaDays))
          }
          if (label.includes('spent in port')) {
            upsertValue(tab.id, field, asDisplayNumber(metrics.annualPortDays))
          }
        })
    })

  return hasChanges ? nextFormData : formData
}
